
import { defineComponent, PropType, computed, toRef, getCurrentInstance, onMounted } from "vue";
import { ViewportStoreModule } from "@stores/viewport.store";
import { getModule } from "vuex-module-decorators";
import { useI18n } from "@composables/i18n";
import { ciTrackingService } from "@services/ci-tracking.service";

const ViewportStore = getModule(ViewportStoreModule);

export default defineComponent({
  props: {
    advisor: {
      type: Object as PropType<UsersV2.AdvisorSearchTile>,
      required: true,
    },
    selectedService: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { t } = useI18n(getCurrentInstance());
    const advisor = toRef(props, "advisor");
    const selectedService = toRef(props, "selectedService");
    const tagsMapping = {
      xs: 2,
      sm: 3,
      md: 8,
      lg: 3,
      xl: 4,
      xxl: 4,
      print: 8,
    };

    const numberOfTags = computed((): number =>
      window.screen.width >= 1920 ? 8 : tagsMapping[ViewportStore.currentViewport]
    );

    const avatarSize = computed((): string => (["xs", "sm"].includes(ViewportStore.currentViewport) ? "xl" : "xxl"));
    const displayTileName = computed((): boolean => ["xs", "sm"].includes(ViewportStore.currentViewport));

    const shouldDisplayDistance = computed((): boolean => advisor.value.location?.distance !== null);
    const city = computed((): string | undefined => advisor.value.location?.city?.toString() || "");
    const postcode = computed((): string | undefined => advisor.value.location?.postcode?.toString());

    const distance = computed((): string => advisor.value.location?.distance?.toString().split(".")[0] + " km" || "");
    const paddingClass = computed((): string => (["xs", "sm"].includes(ViewportStore.currentViewport) ? "p-3" : "p-4"));

    const fontClass = computed((): string => {
      if (["xs", "sm"].includes(ViewportStore.currentViewport)) return "text-sm";
      return "text-md";
    });

    const services = computed((): UsersV2.AdvisoryServiceItem[] => {
      const advisoryServices = advisor.value.advisory_services;
      advisoryServices?.sort((item1, item2) => item1.title.localeCompare(item2.title));

      let result = [];

      if (advisoryServices!.length <= numberOfTags.value) {
        result = advisoryServices!;
      } else result = advisoryServices!.slice(0, numberOfTags.value);

      if (selectedService.value && advisorHasSelectedService()) {
        result = result.filter((service) => service.title !== selectedService.value);
        const selectedValue = advisoryServices!.filter((service) => service.title === selectedService.value);
        result.unshift(...selectedValue);
      }

      return result.length > numberOfTags.value ? result.slice(0, numberOfTags.value) : result;
    });

    onMounted(() => {
      ciTrackingService.track({
        event: {
          action: "impression",
          value: null,
        },
        target: {
          name: "UserProfileAdvisor",
          identifier: props.advisor.id.toString(),
          url: props.advisor.url,
        },
        source: {
          name: "AdvisorSearch",
          identifier: location.href,
        },
      });
    });

    const advisorHasSelectedService = (): boolean => {
      const advisoryServices = advisor.value.advisory_services;

      if (selectedService.value !== "")
        return advisoryServices?.filter((service) => service.title === selectedService.value).length === 1;

      return false;
    };

    const moreServices = (): string =>
      `+ ${advisor.value.advisory_services!.length - services.value.length} ` + `${t("user.settings.privacy.more")}`;

    return {
      avatarSize,
      displayTileName,
      city,
      distance,
      postcode,
      services,
      moreServices,
      numberOfTags,
      paddingClass,
      fontClass,
      advisorHasSelectedService,
      shouldDisplayDistance,
    };
  },
});
